import { useEffect, useRef, useState } from "react";
import {
  frontPageGifs,
  tgExploreProducts,
  unsubscribeSteps,
} from "lib/frontPageData";
import ActivationGuidePanel from "./ActivationGuidePanel";
import ExtensionProductCard from "./ExtensionProductCard";
import GuidePanel from "./GuidePanel";
import { useStore } from "lib/store/store";

export type FrontTabId = "activate" | "unsubscribe" | "tg-explore";

type NavTheme = "green" | "red" | "telegram";

type NavItem = {
  id: FrontTabId;
  label: string;
  icon: string;
  theme: NavTheme;
};

const NAV: NavItem[] = [
  {
    id: "activate",
    label: "Activate My Premium Plan",
    icon: "fas fa-bolt",
    theme: "green",
  },
  {
    id: "unsubscribe",
    label: "Unsubscribe from My Premium Plan",
    icon: "fas fa-circle-xmark",
    theme: "red",
  },
  {
    id: "tg-explore",
    label: "Know More About Telegram Web Extensions",
    icon: "fab fa-telegram",
    theme: "telegram",
  },
];

const SHELL =
  "group flex w-full items-center gap-3 rounded-xl border px-4 py-3.5 text-left transition-all duration-300 sm:gap-4 sm:px-5 sm:py-4";

const THEME_HOVER: Record<NavTheme, string> = {
  green:
    "border-[var(--dash-border)] bg-[var(--dash-surface)] text-[var(--dash-text)] hover:-translate-y-1 hover:scale-[1.01] hover:border-[#2ecc71] hover:text-[var(--dash-text-strong)] hover:shadow-[0_0_24px_rgba(46,204,113,0.4)]",
  red:
    "border-[var(--dash-border)] bg-[var(--dash-surface)] text-[var(--dash-text)] hover:-translate-y-1 hover:scale-[1.01] hover:border-[#ff4444] hover:text-[var(--dash-text-strong)] hover:shadow-[0_0_24px_rgba(255,68,68,0.35)]",
  telegram:
    "border-[var(--dash-border)] bg-[var(--dash-surface)] text-[var(--dash-text)] hover:-translate-y-1 hover:scale-[1.01] hover:border-[#00a2f3] hover:text-[var(--dash-text-strong)] hover:shadow-[0_0_24px_rgba(0,162,243,0.4)]",
};

const THEME_ACTIVE: Record<NavTheme, string> = {
  green:
    "border-[#2ecc71] bg-[#238636]/20 text-[var(--dash-text-strong)] shadow-[0_0_20px_rgba(46,204,113,0.35)]",
  red:
    "border-[#ff4444] bg-[#ff4444]/10 text-[var(--dash-text-strong)] shadow-[0_0_20px_rgba(255,68,68,0.3)]",
  telegram:
    "border-[#00a2f3] bg-[#0088cc]/20 text-[var(--dash-text-strong)] shadow-[0_0_20px_rgba(0,162,243,0.35)]",
};

const THEME_ICON: Record<NavTheme, string> = {
  green: "text-[var(--dash-text-muted)] group-hover:text-[#2ecc71]",
  red: "text-[var(--dash-text-muted)] group-hover:text-[#ff4444]",
  telegram: "text-[var(--dash-text-muted)] group-hover:text-[#00a2f3]",
};

const THEME_ICON_ACTIVE: Record<NavTheme, string> = {
  green: "text-[#2ecc71]",
  red: "text-[#ff4444]",
  telegram: "text-[#00a2f3]",
};

function navButtonClass(active: boolean, theme: NavTheme) {
  return `${SHELL} ${active ? THEME_ACTIVE[theme] : THEME_HOVER[theme]}`;
}

export default function FrontPageTabs() {
  const [tab, setTab] = useState<FrontTabId | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const { frontTab } = useStore();
  const renderNavButton = ({ id, label, icon, theme }: NavItem) => {
    const active = tab === id;
    return (
      <button
        key={id}
        type="button"
        onClick={() => setTab(id)}
        className={navButtonClass(active, theme)}
      >
        <i
          className={`${icon} shrink-0 text-lg transition-colors duration-300 sm:text-xl ${
            active ? THEME_ICON_ACTIVE[theme] : THEME_ICON[theme]
          }`}
          aria-hidden
        />
        <span className="text-sm font-semibold leading-snug sm:text-base">{label}</span>
      </button>
    );
  };

  const renderNavButtons = (items: NavItem[]) => items.map(renderNavButton);

  useEffect(() => {
    if (!frontTab) return;
    const isValid = NAV.some((item) => item.id === frontTab);
    if (isValid) {
      setTab(frontTab as FrontTabId);
      document.getElementById("tabs-section")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [frontTab]);

  useEffect(() => {
    if (tab === null) return;
    const el = panelRef.current;
    if (!el) return;
    let alive = true;
    const id = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        if (alive) el.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    });
    return () => {
      alive = false;
      cancelAnimationFrame(id);
    };
  }, [tab]);

  return (
    <>
      <div className="mb-16 border-b border-[var(--dash-border)] pb-10">
        <div className="mx-auto flex w-full max-w-5xl flex-col gap-3 sm:gap-4 md:hidden">
          {renderNavButtons(NAV)}
        </div>
        <div className="mx-auto hidden w-full max-w-5xl flex-col gap-4 md:flex">
          <div className="grid grid-cols-2 gap-4">{renderNavButtons(NAV.slice(0, 2))}</div>
          <div className="grid grid-cols-1 gap-4">{renderNavButtons(NAV.slice(2))}</div>
        </div>
      </div>

      {tab !== null && (
        <div ref={panelRef} className="scroll-mt-6 md:scroll-mt-8">
          {tab === "activate" && (
            <div>
              <ActivationGuidePanel />
            </div>
          )}

          {tab === "unsubscribe" && (
            <div>
              <GuidePanel
                heading="Unsubscribe"
                steps={unsubscribeSteps}
                gifSrc={frontPageGifs.unsubscribe}
              />
            </div>
          )}

          {tab === "tg-explore" && (
            <div>
              <h2 className="my-5 mb-8 flex items-center text-2xl font-bold text-[var(--dash-text-strong)] before:mr-4 before:h-11 before:w-2 before:shrink-0 before:rounded before:bg-[#0088cc] before:content-[''] md:text-[2.2rem]">
                Telegram Hub Extensions
              </h2>
              <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
                {tgExploreProducts.map((product) => (
                  <ExtensionProductCard key={product.title} product={product} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}
